import { type ReactNode, forwardRef } from 'react';
import { Box, Button, Stack, styled } from '@lingoda/ui';
import { XIcon } from '@lingoda/ui/icons/lingodaIcons';

type Severity = 'warn' | 'error';

interface NotificationBarProps {
    severity: Severity;
    icon: ReactNode;
    children: ReactNode;
    onDismiss?: () => void;
    style?: React.CSSProperties;
}

export const NotificationBar = forwardRef<HTMLDivElement, NotificationBarProps>(
    ({ severity, icon, children, onDismiss, style }, ref) => (
        <BarBgStyled ref={ref} severity={severity} style={style}>
            <InfoStyled spacing={2} direction="row">
                <IconWrapperStyled severity={severity}>{icon}</IconWrapperStyled>
                {children}
            </InfoStyled>
            {onDismiss && (
                <CloseButton onClick={onDismiss} size="small">
                    <XIcon size={24} />
                </CloseButton>
            )}
        </BarBgStyled>
    ),
);

NotificationBar.displayName = 'NotificationBar';

const BarBgStyled = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'severity',
})<{ severity: Severity }>(({ theme, severity }) => ({
    justifyContent: 'space-between',
    flexDirection: 'row',
    width: '100%',
    display: 'flex',
    padding: theme.spacing(3),
    alignItems: 'flex-start',
    backgroundColor:
        severity === 'error'
            ? theme.colors.background['danger-subtle']
            : theme.colors.background['warning-subtle'],

    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(2),
    },
}));

const InfoStyled = styled(Stack)({
    height: '100%',
    alignItems: 'flex-start',
});

const IconWrapperStyled = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'severity',
})<{ severity: Severity }>(({ theme, severity }) => ({
    display: 'flex',
    color: severity === 'error' ? theme.colors.text.danger : theme.colors.text.warning,
}));

const CloseButton = styled(Button)(({ theme }) => ({
    color: theme.palette.text.secondary,
    padding: 0,
}));
